/**
 * Tela de Atendimento do Cliente (PWA)
 * - Botão "Solicitar atendimento" abre/reabre o ticket
 * - Lista mensagens com polling a cada 5s
 * - Status visual "aguardando atendente" quando ainda não houve resposta
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Send, MessageCircle, Loader2, Paperclip, FileText, Download, X } from 'lucide-react';

const POLL_MS = 5000;
const MAX_FILE_MB = 10;

// Ícones por categoria — sincronizado com backend `CATEGORIAS_VALIDAS`
const ICONE_CAT = {
  problema_veiculo: '🛠️',
  moto_bloqueada: '🔒',
  pagamento_recusado: '❌',
  debito_em_atraso: '⏰',
  outro: '💬',
};

const fmtHora = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`;
  return `${(b / 1024 / 1024).toFixed(1)}MB`;
};

const ClienteAtendimentoScreen = ({ api, onVoltar }) => {
  const [carregando, setCarregando] = useState(true);
  const [atendimento, setAtendimento] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMsg, setNovaMsg] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  // Seletor de categoria — só aparece quando não há atendimento aberto
  const [mostrarSeletor, setMostrarSeletor] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [uploadando, setUploadando] = useState(false);
  const [imagemAmpliada, setImagemAmpliada] = useState(null);
  const fimRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const abrirOuCarregar = useCallback(async () => {
    setErro(null);
    try {
      const { data: lista } = await api.get('/cliente/atendimento/mensagens');
      if (lista.atendimento) {
        // Já tem atendimento ativo → vai direto pro chat
        setAtendimento(lista.atendimento);
        setMensagens(lista.mensagens || []);
        setMostrarSeletor(false);
      } else {
        // Sem atendimento → mostra seletor de categoria
        setMostrarSeletor(true);
        try {
          const { data: cat } = await api.get('/cliente/atendimento/categorias');
          setCategorias(cat?.categorias || []);
        } catch {
          // Fallback caso o endpoint não responda
          setCategorias([
            { chave: 'problema_veiculo', label: 'Problema com o veículo' },
            { chave: 'moto_bloqueada', label: 'Moto bloqueada' },
            { chave: 'pagamento_recusado', label: 'Pagamento recusado' },
            { chave: 'debito_em_atraso', label: 'Débito em atraso' },
            { chave: 'outro', label: 'Outro assunto' },
          ]);
        }
      }
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao abrir atendimento');
    } finally {
      setCarregando(false);
    }
  }, [api]);

  const abrirComCategoria = async (categoria) => {
    setCarregando(true);
    try {
      const { data: novo } = await api.post('/cliente/atendimento/abrir', { categoria });
      setAtendimento(novo.atendimento);
      setMensagens(novo.mensagens || []);
      setMostrarSeletor(false);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao abrir atendimento');
    } finally {
      setCarregando(false);
    }
  };

  const polling = useCallback(async () => {
    try {
      const { data } = await api.get('/cliente/atendimento/mensagens');
      if (data.atendimento) {
        setAtendimento(data.atendimento);
        setMensagens(data.mensagens || []);
      }
    } catch (e) {
      // Polling silencioso por design, mas registra em debug para diagnóstico
      console.debug('Polling de atendimento falhou:', e?.message);
    }
  }, [api]);

  const enviar = async () => {
    const txt = novaMsg.trim();
    if (!txt || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const { data } = await api.post('/cliente/atendimento/mensagem', { texto: txt });
      setMensagens(prev => [...prev, data.mensagem]);
      setNovaMsg('');
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const enviarArquivo = async (file) => {
    if (!file || uploadando) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErro(`Arquivo muito grande (${(file.size/1024/1024).toFixed(1)}MB). Máximo ${MAX_FILE_MB}MB.`);
      return;
    }
    setUploadando(true);
    setErro(null);
    const fd = new FormData();
    fd.append('arquivo', file);
    try {
      const { data } = await api.post('/cliente/atendimento/mensagem/arquivo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMensagens(prev => [...prev, data.mensagem]);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploadando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    abrirOuCarregar();
  }, [abrirOuCarregar]);

  useEffect(() => {
    if (!atendimento) return;
    pollRef.current = setInterval(polling, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [atendimento, polling]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  if (carregando) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando atendimento...
      </div>
    );
  }

  // Tela de seleção de categoria — antes de abrir o atendimento
  if (mostrarSeletor) {
    return (
      <div className="flex flex-col" data-testid="cliente-atendimento-seletor" style={{ minHeight: 'calc(100dvh - 72px)' }}>
        <div
          className="px-5 pt-6 pb-4 flex items-center gap-3 sticky top-0 z-10"
          style={{ background: 'linear-gradient(180deg, #0F1626 0%, #060A14 100%)', borderBottom: '1px solid rgba(0, 255, 102, 0.2)' }}
        >
          <button onClick={onVoltar} className="text-slate-300 text-sm" data-testid="atendimento-voltar">
            ← Voltar
          </button>
          <div className="flex-1">
            <h1 className="text-base font-semibold text-white flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#00FF66]" />
              Atendimento
            </h1>
          </div>
        </div>

        <div className="px-5 py-6 flex-1">
          <h2 className="text-xl font-bold text-white mb-1">Como podemos te ajudar?</h2>
          <p className="text-sm text-slate-400 mb-6">
            Escolha o assunto pra agilizar o atendimento
          </p>

          <div className="space-y-3">
            {categorias.map(cat => (
              <button
                key={cat.chave}
                onClick={() => abrirComCategoria(cat.chave)}
                className="w-full rounded-2xl px-4 py-4 text-left flex items-center justify-between transition active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 255, 102, 0.08) 0%, rgba(0, 255, 102, 0.03) 100%)',
                  border: '1px solid rgba(0, 255, 102, 0.25)',
                  color: '#E2E8F0',
                }}
                data-testid={`cliente-categoria-${cat.chave}`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl" aria-hidden>{ICONE_CAT[cat.chave] || '💬'}</span>
                  <span className="font-medium">{cat.label}</span>
                </span>
                <span className="text-[#00FF66] text-xl" aria-hidden>›</span>
              </button>
            ))}
          </div>

          {erro && (
            <div className="mt-4 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="atendimento-erro">
              {erro}
            </div>
          )}
        </div>
      </div>
    );
  }

  const aguardandoResposta = mensagens.length > 0 &&
    !mensagens.some(m => m.autor === 'admin');

  return (
    <div className="flex flex-col h-full" data-testid="cliente-atendimento-screen" style={{ minHeight: 'calc(100dvh - 72px)' }}>
      {/* HEADER */}
      <div
        className="px-5 pt-6 pb-4 flex items-center gap-3 sticky top-0 z-10"
        style={{ background: 'linear-gradient(180deg, #0F1626 0%, #060A14 100%)', borderBottom: '1px solid rgba(0, 255, 102, 0.2)' }}
      >
        <button onClick={onVoltar} className="text-slate-300 text-sm" data-testid="atendimento-voltar">
          ← Voltar
        </button>
        <div className="flex-1">
          <h1 className="text-base font-semibold text-white flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#00FF66]" />
            Atendimento
          </h1>
          {atendimento && (
            <p className="text-xs text-slate-400">
              {atendimento.categoria_label && (
                <span className="inline-block mr-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide"
                  style={{ background: 'rgba(0, 255, 102, 0.15)', color: '#A7F3D0', border: '1px solid rgba(0, 255, 102, 0.3)' }}>
                  {ICONE_CAT[atendimento.categoria] || '💬'} {atendimento.categoria_label}
                </span>
              )}
              Status: <span className="text-[#00FF66]">{atendimento.status === 'aberto' ? 'aguardando' : atendimento.status}</span>
            </p>
          )}
        </div>
      </div>

      {/* AVISO AGUARDANDO */}
      {aguardandoResposta && (
        <div
          className="mx-5 mt-4 rounded-2xl p-4 border"
          style={{ background: 'rgba(0, 255, 102, 0.08)', borderColor: 'rgba(0, 255, 102, 0.25)' }}
          data-testid="atendimento-aguardando-banner"
        >
          <p className="text-sm text-emerald-200">
            ⏳ Aguarde alguns instantes — um atendente vai responder em breve.
          </p>
        </div>
      )}

      {/* LISTA DE MENSAGENS */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="atendimento-lista-mensagens">
        {mensagens.map(msg => (
          <MensagemBubble
            key={msg.id}
            msg={msg}
            api={api}
            onAbrirImagem={(url, nome) => setImagemAmpliada({ url, nome })}
          />
        ))}
        <div ref={fimRef} />
      </div>

      {/* INPUT */}
      <div
        className="px-3 py-3 flex items-end gap-2 sticky bottom-0"
        style={{
          background: 'linear-gradient(180deg, #060A14 0%, #0F1626 100%)',
          borderTop: '1px solid rgba(148, 163, 184, 0.15)',
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          className="hidden"
          onChange={(e) => enviarArquivo(e.target.files?.[0])}
          data-testid="atendimento-arquivo-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadando || enviando}
          className="rounded-full p-2.5 disabled:opacity-40 transition shrink-0"
          style={{ background: 'rgba(0, 255, 102, 0.12)', color: '#00FF66', border: '1px solid rgba(0, 255, 102, 0.3)' }}
          title="Enviar foto ou PDF (10MB max)"
          aria-label="Anexar arquivo"
          data-testid="atendimento-anexo-btn"
        >
          {uploadando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
        <textarea
          value={novaMsg}
          onChange={(e) => setNovaMsg(e.target.value)}
          placeholder="Digite sua mensagem..."
          rows={1}
          maxLength={2000}
          className="flex-1 rounded-2xl px-4 py-2.5 text-white text-sm resize-none focus:outline-none"
          style={{ background: 'rgba(15, 22, 38, 0.8)', border: '1px solid rgba(148, 163, 184, 0.2)', maxHeight: '100px' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
          }}
          data-testid="atendimento-input"
        />
        <button
          onClick={enviar}
          disabled={enviando || !novaMsg.trim()}
          className="rounded-full p-2.5 disabled:opacity-40 transition"
          style={{ background: '#00FF66', color: '#060A14' }}
          data-testid="atendimento-btn-enviar"
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {erro && (
        <div className="mx-5 mb-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2" data-testid="atendimento-erro">
          {erro}
        </div>
      )}

      {/* LIGHTBOX */}
      {imagemAmpliada && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setImagemAmpliada(null)}
          data-testid="cliente-lightbox-imagem"
        >
          <button
            onClick={() => setImagemAmpliada(null)}
            className="absolute top-4 right-4 text-white bg-slate-800/80 hover:bg-slate-700 rounded-full p-2"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={imagemAmpliada.url}
            alt={imagemAmpliada.nome}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const MensagemBubble = ({ msg, api, onAbrirImagem }) => {
  const isCliente = msg.autor === 'cliente';
  const isSistema = msg.autor === 'sistema';
  const isAdmin = msg.autor === 'admin';
  const temArquivo = !!msg.arquivo_key;

  if (isSistema) {
    return (
      <div className="flex justify-center">
        <div
          className="rounded-full px-3 py-1.5 text-xs"
          style={{ background: 'rgba(0, 255, 102, 0.1)', border: '1px solid rgba(0, 255, 102, 0.25)', color: '#A7F3D0' }}
        >
          {msg.texto}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isCliente ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[80%] rounded-2xl px-4 py-2.5"
        style={
          isCliente
            ? { background: 'linear-gradient(135deg, #00FF66 0%, #00D955 100%)', color: '#060A14' }
            : { background: 'rgba(30, 41, 59, 0.9)', color: '#E2E8F0', border: '1px solid rgba(148, 163, 184, 0.15)' }
        }
      >
        {isAdmin && (
          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-70">
            {msg.autor_nome || 'Atendente'}
          </p>
        )}
        {temArquivo && (
          <ClienteAnexoPreview msg={msg} api={api} isCliente={isCliente} onAbrirImagem={onAbrirImagem} />
        )}
        {msg.texto && (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.texto}</p>
        )}
        <p className={`text-[10px] mt-1 ${isCliente ? 'text-slate-800' : 'text-slate-400'} text-right`}>
          {fmtHora(msg.criado_em)}
        </p>
      </div>
    </div>
  );
};

const ClienteAnexoPreview = ({ msg, api, isCliente, onAbrirImagem }) => {
  const [url, setUrl] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let alivo = true;
    api.get(`/atendimento/mensagens/${msg.id}/arquivo`)
      .then(({ data }) => { if (alivo) setUrl(data.url); })
      .catch(() => { if (alivo) setErro(true); })
      .finally(() => { if (alivo) setCarregando(false); });
    return () => { alivo = false; };
  }, [msg.id, api]);

  if (carregando) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs opacity-70">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando anexo...
      </div>
    );
  }

  if (erro || !url) {
    return <p className="text-xs italic text-red-400">Falha ao carregar anexo</p>;
  }

  if (msg.arquivo_eh_imagem) {
    return (
      <button
        onClick={() => onAbrirImagem?.(url, msg.arquivo_nome)}
        className="block mb-1 rounded-lg overflow-hidden hover:opacity-90 transition"
        data-testid={`cliente-anexo-imagem-${msg.id.slice(0,8)}`}
      >
        <img
          src={url}
          alt={msg.arquivo_nome}
          className="max-w-[240px] max-h-[240px] object-cover rounded-lg"
          loading="lazy"
        />
      </button>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-lg px-2 py-2 mb-1 transition ${
        isCliente ? 'bg-slate-900/20 hover:bg-slate-900/30' : 'bg-slate-600/40 hover:bg-slate-600/60'
      }`}
      data-testid={`cliente-anexo-arquivo-${msg.id.slice(0,8)}`}
    >
      <FileText className="w-6 h-6 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">{msg.arquivo_nome || 'arquivo.pdf'}</p>
        <p className="text-[10px] opacity-70">{fmtBytes(msg.arquivo_tamanho)} • Toque pra abrir</p>
      </div>
      <Download className="w-4 h-4 shrink-0 opacity-70" />
    </a>
  );
};

export default ClienteAtendimentoScreen;
